import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/i18n';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { SupportedLocale } from '@/types/supported-locale.type';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { computeAgencyMarginBreakdown } from '@/utils/estimates/computeAgencyMarginBreakdown';
import { computeInternationalFlightsTotal } from '@/utils/estimates/internationalFlightsPricing';
import { addDays } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { getTranslatedCountry } from '../../../../shared/CountriesData';
import { SelectedEstimateOption } from '../../types/estimates/SelectedEstimateOptionType';
import CircuitDetailsSummaryCard from '../distributorCircuits/CircuitDetailsSummaryCard';
import EstimateAgencyCards from './details/EstimateAgencyCards';
import EstimateAgencyMarginsCard from './details/EstimateAgencyMarginsCard';
import EstimateCustomParagraphCard from './details/EstimateCustomParagraphCard';
import EstimateDaysCards from './details/EstimateDaysCards';
import EstimateDetailsSideCard from './details/EstimateDetailsSideCard';
import EstimateInternationalFlightsCard from './details/EstimateInternationalFlightsCard';
import EstimateSummaryContactCard from './details/EstimateSummaryContactCard';
import EstimateSummaryParticipantsCard from './details/EstimateSummaryParticipantsCard';

export type OptionalBenefitComputationFn = (
  benefit: BenefitFormData,
  date: Date,
  currIndex: number,
  nbDays?: number,
  nbAdultsOverride?: number,
  nbAdolescentsOverride?: number,
  nbChildrenOverride?: number,
  selectedHotelPensionType?: string,
) => number;

interface EstimateDetailsAndOptionsProps {
  estimateTitle?: string;
  circuit: CircuitFormData;
  arrivalDate: Date;
  adults: number;
  children: number;
  totalPrice: number;
  control: Control<EstimateFormData>;
  estimateMarginRate: number;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  goToLastStep: () => void;
  saveEstimate: (
    selectedOptions?: Record<string, SelectedEstimateOption>,
  ) => void;
  sendReservationRequest: (
    selectedOptions?: Record<string, SelectedEstimateOption>,
  ) => void;
  status: EstimateStatus;
  isViewMode?: boolean;
  isContactFormDisabled?: boolean;
  shouldHideInternationalFlights?: boolean;
  accessToken?: string;
  isIncomingReservation?: boolean;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  currency?: string;
  creationDate?: Date;
  isCircuitPage?: boolean;
  isValid?: boolean;
  errorsLength?: number;
  publishCircuit?: () => void;
  shouldHideCustomParagraph?: boolean;
  secondaryBtnText?: string;
  onSecondaryClick?: () => void;
  language?: SupportedLocale;
  isEditableCircuit?: boolean;
  isAgencyView?: boolean;
  isToView?: boolean;
  isClientLink?: boolean;
  /** Souplesse TO : hide the "Confirmer la demande de réservation" button (client link). */
  hideReservationConfirm?: boolean;
  /** Souplesse TO (agency view) : the final client has already sent his request. */
  clientHasSentRequest?: boolean;
  /** Souplesse TO (client view) : the client has already sent his request to the agency. */
  clientRequestAlreadySent?: boolean;
  allowAgencyMarginRate?: boolean;
  allowAgencyInternationalFlights?: boolean;
  allowAgencyAdditionalMargin?: boolean;
  /** Re-scales option delta prices when the agency margin changes live. */
  distributorMarginPriceFactor?: number;
  /** Circuit + options with general agency margin only (margin summary). */
  circuitWithGeneralMarginForSummary?: number;
  frozenAdditionalMarginType?: 'FIXED' | 'PERCENT';
  frozenAdditionalMarginValue?: number | null;
  agencyAmount?: number | null;
  hidePriceIncludesExcludes?: boolean;
  hideModificationCancellationConditions?: boolean;
  mutePriceIncludesExcludes?: boolean;
  muteModificationCancellationConditions?: boolean;
  titleMuted?: boolean;
  /** Currency of selectedOptions deltas (defaults to EUR for estimates). */
  optionsCurrency?: string;
}

const EstimateDetailsAndOptions = ({
  estimateTitle,
  circuit,
  arrivalDate,
  adults,
  children,
  totalPrice,
  control,
  selectedOptions,
  setSelectedOptions,
  computeOptionalBenefitPrice,
  estimateMarginRate: _estimateMarginRate,
  goToLastStep,
  saveEstimate,
  sendReservationRequest,
  status,
  isViewMode = false,
  isContactFormDisabled = false,
  shouldHideInternationalFlights = false,
  accessToken,
  isIncomingReservation = false,
  currency = 'EUR',
  optionsCurrency = 'EUR',
  creationDate,
  isCircuitPage = false,
  isValid,
  errorsLength,
  publishCircuit,
  shouldHideCustomParagraph = false,
  secondaryBtnText,
  onSecondaryClick,
  language,
  isEditableCircuit = false,
  isAgencyView = false,
  isToView = false,
  isClientLink = false,
  hideReservationConfirm = false,
  clientHasSentRequest = false,
  clientRequestAlreadySent = false,
  allowAgencyMarginRate = false,
  allowAgencyInternationalFlights = false,
  allowAgencyAdditionalMargin = false,
  distributorMarginPriceFactor = 1,
  circuitWithGeneralMarginForSummary,
  frozenAdditionalMarginType,
  frozenAdditionalMarginValue,
  agencyAmount,
  hidePriceIncludesExcludes = false,
  hideModificationCancellationConditions = false,
  mutePriceIncludesExcludes = false,
  muteModificationCancellationConditions = false,
  titleMuted = false,
}: EstimateDetailsAndOptionsProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const image = useMemo(
    () => (circuit.mainImage as string) ?? circuit.images?.[0] ?? undefined,
    [circuit.mainImage, circuit.images],
  );

  const countries = circuit.countries
    .map(({ country }) => country.label)
    .map((country) =>
      getTranslatedCountry(country, language ?? currentLanguage),
    )
    .join(', ');

  const departureDate = useMemo(
    () => addDays(arrivalDate, circuit.nbDays - 1),
    [arrivalDate, circuit.nbDays],
  );

  const agencyEntersInternationalFlights =
    isAgencyView && allowAgencyInternationalFlights;

  const internationalFlightPrice = useWatch({
    control,
    name: agencyEntersInternationalFlights
      ? 'agencyInternationalFlightsPrice'
      : 'internationalFlightsPrice',
  });

  const internationalFlightsNonMarginedTaxes = useWatch({
    control,
    name: agencyEntersInternationalFlights
      ? 'agencyInternationalFlightsNonMarginedTaxes'
      : 'internationalFlightsNonMarginedTaxes',
  });

  const internationalFlightsMarginRate = useWatch({
    control,
    name: agencyEntersInternationalFlights
      ? 'agencyInternationalFlightsMarginRate'
      : 'internationalFlightsMarginRate',
  });

  const isInternationalFlightPriceMargined = useWatch({
    control,
    name: agencyEntersInternationalFlights
      ? 'agencyMarginRateExistsForInternationalFlights'
      : 'marginRateExistsForInternationalFlights',
  });

  const isModificationBlocked = useMemo(
    () => status !== EstimateStatus.PENDING,
    [status],
  );

  const usesFrozenAgencyTotal = useMemo(
    () =>
      (isAgencyView || isClientLink) &&
      agencyAmount != null &&
      status !== EstimateStatus.PENDING,
    [agencyAmount, isAgencyView, isClientLink, status],
  );

  const liveInternationalFlightsTotal = useMemo(() => {
    if (usesFrozenAgencyTotal) {
      return 0;
    }
    if (isToView && allowAgencyInternationalFlights) {
      return 0;
    }
    if (isClientLink) {
      const price =
        typeof internationalFlightPrice === 'number'
          ? internationalFlightPrice
          : Number(internationalFlightPrice) || 0;
      // Client link API already merges taxes + margin into this field (hideRates).
      return price;
    }
    return computeInternationalFlightsTotal({
      nonMarginedTaxes: internationalFlightsNonMarginedTaxes,
      htAmount: internationalFlightPrice,
      isMargined: isInternationalFlightPriceMargined,
      flightsMarginRate: internationalFlightsMarginRate,
    });
  }, [
    internationalFlightPrice,
    internationalFlightsMarginRate,
    internationalFlightsNonMarginedTaxes,
    isClientLink,
    isInternationalFlightPriceMargined,
    isToView,
    allowAgencyInternationalFlights,
    usesFrozenAgencyTotal,
  ]);

  const priceIncludes = useWatch({
    control,
    name: 'circuit.priceIncludes',
  });

  const priceExcludes = useWatch({
    control,
    name: 'circuit.priceExcludes',
  });

  const modificationConditions = useWatch({
    control,
    name: 'circuit.modificationsConditions',
  });

  const cancellationConditions = useWatch({
    control,
    name: 'circuit.cancellationConditions',
  });

  const watchedAgencyMarginRate = useWatch({
    control,
    name: 'agencyMarginRate',
    disabled: !isAgencyView,
  });
  const watchedAdditionalMarginType = useWatch({
    control,
    name: 'agencyAdditionalMarginType',
    disabled: !isAgencyView,
  });
  const watchedAdditionalMarginValue = useWatch({
    control,
    name: 'agencyAdditionalMarginValue',
    disabled: !isAgencyView,
  });

  const agencyMarginBreakdown = useMemo(() => {
    if (
      !isAgencyView ||
      isClientLink ||
      (!allowAgencyMarginRate &&
        !allowAgencyAdditionalMargin &&
        !allowAgencyInternationalFlights)
    ) {
      return null;
    }

    const agencyMarginRate =
      watchedAgencyMarginRate != null &&
      !Number.isNaN(Number(watchedAgencyMarginRate))
        ? Number(watchedAgencyMarginRate)
        : 0;

    const additionalType =
      isModificationBlocked && frozenAdditionalMarginType
        ? frozenAdditionalMarginType
        : ((watchedAdditionalMarginType as 'FIXED' | 'PERCENT' | undefined) ??
          'FIXED');

    const additionalValue = isModificationBlocked
      ? frozenAdditionalMarginValue
      : watchedAdditionalMarginValue != null &&
          !Number.isNaN(Number(watchedAdditionalMarginValue))
        ? Number(watchedAdditionalMarginValue)
        : null;

    let circuitWithGeneralMargin =
      circuitWithGeneralMarginForSummary ?? totalPrice;
    if (
      circuitWithGeneralMarginForSummary == null &&
      allowAgencyAdditionalMargin &&
      additionalValue != null &&
      additionalValue > 0
    ) {
      circuitWithGeneralMargin =
        additionalType === 'PERCENT'
          ? roundUpPrice(totalPrice / (1 + additionalValue / 100))
          : roundUpPrice(totalPrice - additionalValue);
    }

    return computeAgencyMarginBreakdown({
      allowAgencyMarginRate,
      allowAgencyAdditionalMargin,
      allowAgencyInternationalFlights,
      agencyMarginRate,
      additionalMarginType: additionalType,
      additionalMarginValue: additionalValue,
      circuitWithGeneralMargin,
      selectedOptions,
      distributorMarginPriceFactor,
      flightsPricing: agencyEntersInternationalFlights
        ? {
            htAmount: internationalFlightPrice,
            isMargined: isInternationalFlightPriceMargined,
            flightsMarginRate: internationalFlightsMarginRate,
          }
        : undefined,
    });
  }, [
    allowAgencyAdditionalMargin,
    allowAgencyInternationalFlights,
    allowAgencyMarginRate,
    agencyEntersInternationalFlights,
    circuitWithGeneralMarginForSummary,
    distributorMarginPriceFactor,
    frozenAdditionalMarginType,
    frozenAdditionalMarginValue,
    internationalFlightPrice,
    internationalFlightsMarginRate,
    isAgencyView,
    isClientLink,
    isInternationalFlightPriceMargined,
    isModificationBlocked,
    selectedOptions,
    totalPrice,
    watchedAdditionalMarginType,
    watchedAdditionalMarginValue,
    watchedAgencyMarginRate,
  ]);

  const adultParticipants = useWatch({
    control,
    name: 'adults',
  });
  const childParticipants = useWatch({
    control,
    name: 'children',
  });

  const makeReservationRequest = useCallback(
    () =>
      !isModificationBlocked
        ? sendReservationRequest(selectedOptions)
        : undefined,
    [isModificationBlocked, selectedOptions, sendReservationRequest],
  );

  const onSaveEstimate = useCallback(
    () => (!isModificationBlocked ? saveEstimate(selectedOptions) : undefined),
    [isModificationBlocked, saveEstimate, selectedOptions],
  );

  const toEntersInternationalFlights =
    isToView && !allowAgencyInternationalFlights;
  const agencyEntersInternationalFlightsConfig =
    isAgencyView && allowAgencyInternationalFlights;
  const agencyViewsToInternationalFlights =
    isAgencyView && !allowAgencyInternationalFlights;
  const isSouplesseView = isToView || isAgencyView;

  const isSouplesseFieldsFrozen = isModificationBlocked;

  // Flight texts: visible on both sides; read-only on the party that does not edit.
  const showCustomParagraph = !isSouplesseView || isToView || isAgencyView;

  const customParagraphAgencyOwnedOnToView =
    isToView && allowAgencyInternationalFlights;

  const customParagraphReadOnly =
    (isSouplesseFieldsFrozen && !isClientLink) ||
    customParagraphAgencyOwnedOnToView ||
    agencyViewsToInternationalFlights;

  const customParagraphUsesSummaryView =
    !customParagraphAgencyOwnedOnToView &&
    ((isClientLink && isSouplesseFieldsFrozen) ||
      (!isSouplesseFieldsFrozen &&
        (isViewMode ||
          (accessToken !== undefined && !isAgencyView && !isToView))));

  // Souplesse TO — page « Demande de réservation » du devis client : le bouton
  // secondaire devient « Adresser ma demande » (enregistre + notifie l'agence).
  const isClientRequestToAgencyView =
    hideReservationConfirm &&
    isViewMode &&
    !isIncomingReservation &&
    !isCircuitPage;
  // Demande déjà adressée : on remplace le bouton par un texte (empêche les
  // clics répétés).
  const showClientRequestSentText =
    isClientRequestToAgencyView && clientRequestAlreadySent;
  // Souplesse TO — côté agence, une fois la demande du client reçue : on met en
  // avant le bouton de confirmation et on affiche un message.
  const showClientRequestReceived =
    isAgencyView &&
    clientHasSentRequest &&
    isViewMode &&
    !isIncomingReservation &&
    !isCircuitPage;

  const EstimateDetailsSideCardMemo = (
    <EstimateDetailsSideCard
      image={image}
      countries={countries}
      title={estimateTitle ?? circuit.name}
      titleMuted={titleMuted}
      nbDays={circuit.nbDays}
      nbNights={circuit.nbNights ?? circuit.nbDays - 1}
      arrivalDate={arrivalDate}
      departureDate={departureDate}
      nbAdults={adults}
      nbChildren={children}
      totalPrice={totalPrice}
      internationalFlightPrice={liveInternationalFlightsTotal}
      selectedOptions={selectedOptions}
      currency={currency}
      distributorMarginPriceFactor={distributorMarginPriceFactor}
      isAllInclusiveTotal={usesFrozenAgencyTotal}
      optionsCurrency={optionsCurrency}
      mainBtnText={
        isCircuitPage
          ? t('estimates.publishCircuit')
          : isViewMode
            ? isIncomingReservation
              ? t('estimates.viewTariffDetails')
              : t('estimates.confirmReservation')
            : t('estimates.reservationRequest')
      }
      onMainClick={
        isCircuitPage
          ? publishCircuit
          : isViewMode
            ? isIncomingReservation
              ? goToLastStep
              : isModificationBlocked
                ? undefined
                : makeReservationRequest
            : goToLastStep
      }
      onSecondaryClick={
        showClientRequestSentText
          ? undefined
          : isClientRequestToAgencyView
            ? isModificationBlocked
              ? undefined
              : makeReservationRequest
            : (onSecondaryClick ??
              (isModificationBlocked || isCircuitPage
                ? undefined
                : onSaveEstimate))
      }
      secondaryReplacementText={
        showClientRequestSentText
          ? t('estimates.clientRequestSentConfirmation')
          : undefined
      }
      secondaryBtnText={
        isClientRequestToAgencyView
          ? t('estimates.sendRequestToAgency')
          : (secondaryBtnText ??
            (isContactFormDisabled
              ? t('estimates.refuseReservation')
              : t('estimates.saveEstimate')))
      }
      isSecondaryBtnDisabled={
        isClientRequestToAgencyView
          ? isModificationBlocked
          : secondaryBtnText
            ? false
            : undefined
      }
      creationDate={creationDate}
      isCircuitPage={isCircuitPage}
      isValid={isValid}
      errorsLength={errorsLength}
      hideMainBtn={
        hideReservationConfirm &&
        isViewMode &&
        !isIncomingReservation &&
        !isCircuitPage
      }
      mainBtnHighlighted={showClientRequestReceived}
      mainBtnNotice={
        showClientRequestReceived
          ? t('estimates.clientRequestReceivedNotice')
          : undefined
      }
    />
  );

  return (
    <div className="flex flex-col lg:flex-row-reverse gap-4 p-6">
      <div className="w-fit h-fit lg:sticky lg:top-20">
        {EstimateDetailsSideCardMemo}
      </div>
      <div className="flex flex-col gap-4 w-full">
        <h1 className="text-2xl font-medium text-black">
          {isCircuitPage
            ? t('estimates.circuitDetail')
            : t('estimates.yourCircuitDetail')}
        </h1>
        {(isAgencyView || isToView) &&
          (allowAgencyMarginRate || allowAgencyAdditionalMargin) && (
            <EstimateAgencyCards
              control={control}
              readOnly={isToView || (isAgencyView && isSouplesseFieldsFrozen)}
              muted={isToView || (isAgencyView && isSouplesseFieldsFrozen)}
              hideValues={isToView}
              allowAgencyMarginRate={allowAgencyMarginRate}
              allowAgencyAdditionalMargin={allowAgencyAdditionalMargin}
            />
          )}
        {!isIncomingReservation &&
          !shouldHideCustomParagraph &&
          showCustomParagraph && (
            <EstimateCustomParagraphCard
              control={control}
              isViewMode={customParagraphUsesSummaryView}
              readOnly={customParagraphReadOnly}
              muted={customParagraphReadOnly}
              showWhenEmpty={
                customParagraphAgencyOwnedOnToView ||
                (isSouplesseFieldsFrozen && !isClientLink)
              }
            />
          )}
        {isToView && allowAgencyInternationalFlights && (
          <EstimateInternationalFlightsCard
            control={control}
            readOnly
            hideValues
            showWhenEmpty
            muted
            taxesFieldName="agencyInternationalFlightsNonMarginedTaxes"
            priceFieldName="agencyInternationalFlightsPrice"
            margedFieldName="agencyMarginRateExistsForInternationalFlights"
            flightsMarginRateFieldName="agencyInternationalFlightsMarginRate"
            title={t('estimates.agencyView.internationalFlights.title')}
          />
        )}
        {toEntersInternationalFlights && (
          <EstimateInternationalFlightsCard
            control={control}
            readOnly={isSouplesseFieldsFrozen}
            showWhenEmpty={isSouplesseFieldsFrozen}
            muted={isSouplesseFieldsFrozen}
            isViewMode={isViewMode && !isSouplesseFieldsFrozen}
          />
        )}
        {agencyEntersInternationalFlightsConfig && (
          <EstimateInternationalFlightsCard
            control={control}
            readOnly={isSouplesseFieldsFrozen}
            showWhenEmpty={isSouplesseFieldsFrozen}
            muted={isSouplesseFieldsFrozen}
            taxesFieldName="agencyInternationalFlightsNonMarginedTaxes"
            priceFieldName="agencyInternationalFlightsPrice"
            margedFieldName="agencyMarginRateExistsForInternationalFlights"
            flightsMarginRateFieldName="agencyInternationalFlightsMarginRate"
            title={t('estimates.agencyView.internationalFlights.title')}
          />
        )}
        {!isAgencyView && !isToView && !shouldHideInternationalFlights && (
          <EstimateInternationalFlightsCard
            control={control}
            readOnly={isSouplesseFieldsFrozen}
            showWhenEmpty={isSouplesseFieldsFrozen}
            muted={isSouplesseFieldsFrozen}
            isViewMode={isViewMode && !isSouplesseFieldsFrozen}
          />
        )}
        {agencyMarginBreakdown && (
          <EstimateAgencyMarginsCard breakdown={agencyMarginBreakdown} />
        )}
        <EstimateDaysCards
          control={control}
          arrivalDate={arrivalDate}
          estimateDays={circuit.circuitDays}
          computeOptionalBenefitPrice={computeOptionalBenefitPrice}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          adultParticipants={adultParticipants}
          childParticipants={childParticipants}
          currency={currency}
          creationDate={creationDate}
          language={language}
          accessToken={accessToken}
          isViewMode={isViewMode || isModificationBlocked}
          showTranslation={isIncomingReservation || isCircuitPage}
          isEditableCircuit={isEditableCircuit}
        />
        {isViewMode && !hidePriceIncludesExcludes && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CircuitDetailsSummaryCard
                title={t('estimates.priceIncludes')}
                summary={priceIncludes ?? ''}
                textBrand
                muted={mutePriceIncludesExcludes}
              />
              <CircuitDetailsSummaryCard
                title={t('estimates.priceExcludes')}
                summary={priceExcludes ?? ''}
                textBrand
                muted={mutePriceIncludesExcludes}
              />
            </div>
          </>
        )}
        {isViewMode && !hideModificationCancellationConditions && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CircuitDetailsSummaryCard
              title={t('estimates.modificationConditions')}
              summary={modificationConditions ?? ''}
              textBrand
              muted={muteModificationCancellationConditions}
            />
            <CircuitDetailsSummaryCard
              title={t('estimates.cancellationConditions')}
              summary={cancellationConditions ?? ''}
              textBrand
              muted={muteModificationCancellationConditions}
            />
          </div>
        )}
        {isViewMode && (
          <>
            <EstimateSummaryParticipantsCard
              control={control}
              adultParticipants={adultParticipants}
              childParticipants={childParticipants}
              isViewMode={isModificationBlocked || isIncomingReservation}
            />
            <EstimateSummaryContactCard
              control={control}
              isViewMode={isContactFormDisabled}
            />
          </>
        )}
        <div className="lg:hidden w-fit">{EstimateDetailsSideCardMemo}</div>
      </div>
    </div>
  );
};

export default EstimateDetailsAndOptions;
